import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from "react-router-dom";
import {
  ArrowBack
} from "@mui/icons-material";
import { Button, Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  Campaign, 
  CharacterOptions,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import LoadingBox from "@components/input/LoadingBox";

import CharacterOptionsInput from "@dndComponents/character/misc/CharacterOptionsInput";
import CampaignOrgInstancesInput from "@dndComponents/character/misc/CampaignOrgInstancesInput";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignEdit() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [logging_in, setLoggingIn] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Campaign>(new Campaign());
  const [mode, setMode] = useState("Home");
  const [do_submit, setDoSubmit] = useState(false);
  const [ping, setPing] = useState(false);
  
  // eslint-disable-next-line
  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      if (api.user === null) {
        const userObjStr = localStorage.getItem("loginUser");
        if (!userObjStr) {
          setNavigateTo("/");
        } else {
          setLoggingIn(true);
          check_login();
        }
      } else {
        load();
      }
    }
  });
    
  useEffect(() => {
    if (do_submit) {
      setDoSubmit(false);
      submit();
    }
    // eslint-disable-next-line
  }, [do_submit]);

  const check_login = () => {
    setTimeout(() => {
      if (api.user === null) {
        check_login();
      } else {
        setLoggingIn(false);
        load();
      }
    }, 1000);
  }

  const submit = () => {
    setProcessing(true);
    const last_updated = common_util.get_time_number();
    obj.last_updated = last_updated;
    if (obj._id && obj._id !== "") {
      api.updateObject("campaign", obj).then(res => {
        setProcessing(false);
      });
    } else {
      obj.owner_id = api.user ? api.user._id : "No Login";
      api.createObject("campaign", obj).then(res => {
        setProcessing(false);
      });
    }
  }

  const load_object = (id: string) => {
    setLoading(true);
    api.getObjects({ data_type: "campaign", filter: { "_id*ObjectIDs": [id] } }).then(res => {
      if (res.length === 1 && res[0] instanceof Campaign) {
        const new_obj = res[0];
        setObj(new_obj);
        setLoading(false);
      }
    });
  }

  const load = () => {
    if (id && id !== "") {
      setLoading(true);
      load_object(id);
    } else {
      // It's here from clicking Create on index.  
      // If the user is not admin, then we need to give the floating feature their id as owner or it will be disabled.
      const new_obj = obj;
      if (api.user) {
        new_obj.owner_id = api.user._id;
        const sb_ids = api.user.source_book_ids;
        new_obj.character_options.source_books = api.source_books.filter(o => !o.homebrew && !o.ua && sb_ids.includes(o._id)).map(o => o._id);
        setObj(new_obj);
      }
    }
  }

  const render_tab = () => {
    let return_me = <Grid>Coming Soon</Grid>;
    const user = api.user;
    if (user) {
      if (mode === "Options") {
        return_me = (
          <Grid
            size={{
              xs: 12
            }}>
            <CharacterOptionsInput 
              obj={obj}
              disabled={processing}
              onChange={(options: CharacterOptions) => {
                const new_obj = obj;
                new_obj.character_options = options.clone();
                setObj(new_obj);
                setDoSubmit(true);
              }}
            />
          </Grid>
        ); 
      } else if (mode === "Organizations") {
        return_me = (
          <Grid
            size={{
              xs: 12
            }}>
            <CampaignOrgInstancesInput 
              campaign={obj}
              onChange={(new_obj: Campaign) => {
                setObj(new_obj);
                setDoSubmit(true);
              }}
            />
          </Grid>
        ); 
      } else {
        return_me = (
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox 
                value={obj.name} 
                message={obj.name.length > 0 ? "" : "Name Invalid"} 
                name="Name"
                onBlur={(value: string) => {
                  const new_obj = obj;
                  new_obj.name = value;
                  setObj(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox 
                value={obj.description} 
                name="Description"
                multiline
                onBlur={(value: string) => {
                  const new_obj = obj;
                  new_obj.description = value;
                  setObj(new_obj);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <Button
                variant="contained"
                color="primary"
                disabled={ processing || obj.name === "" }
                onClick={submit}>
                Submit
              </Button>
              <Button
                variant="contained"
                disabled={processing}
                style={{ marginLeft: "4px" }}
                onClick={ () => { 
                  setNavigateTo(`/campaign`);
                }}>
                Cancel
              </Button>
            </Grid>
          </>
        ); 
      }
    }
    return return_me;
  }
  
  const height = window.innerHeight - 64;
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!api.online) {
    return (
      <>Campaign Editing only works while online.  Try reconnecting.</>
    );
  } else if (!fetched || loading || logging_in) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else { 
    const menu = ["Home","Options","Organizations"];
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            size={{
              xs: 3
            }}>
            <Tooltip title={`Back to Campaigns`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  setNavigateTo(`/campaign`);
                }}>
                <ArrowBack/>
              </Fab>
            </Tooltip> 
          </Grid>
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">
            { obj._id === "" ? "Create Campaign" : `Edit ${obj.name}` }
          </span>
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <CenteredMenu
            options={menu}
            selected={mode}
            onSelect={setMode}
          />
        </Grid>
        { render_tab() }
      </Grid>
    ); 
  }
}